// useRateChange — record a rate change through the ONE function that writes one.
//
// WHAT CHANGED, 2026-09-08. This hook used to INSERT a Pending row and wait for
// somebody to approve it. Ben's ruling that day: "When changed, the change takes
// effect. Finance, National AM, and execs are notified. Stop making additional
// processes." The evidence agreed with him — in thirteen months the approval
// step had never once stopped a change: 130 approved, one withdrawn by the
// person who raised it, ZERO rejected, and 23 of the 130 approved by the same
// person who proposed them. It was a day's delay, not a control.
//
// ⛔ THE CLIENT NO LONGER WRITES THE TABLE. It calls record_rate_change(), which
// is the only thing that writes a rate. Every screen that can start a rate
// change is a button to that function (TCSD-0188). An INSERT from here would be
// a second door, and a second door is where a change goes missing.
//
// The date is the point. A change recorded for today or any past date takes
// effect on save; a future one is booked and lands on its own night. ⛔ A past
// date is never refused and never warned as late — 46 of 58 dated sub changes
// on file were recorded after the fact. The system exists to make the record
// right before pay runs, not to police when the account manager typed it.
// ============================================================================

import { useCallback } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';

export interface RateChangeInput {
  /** The LINE the rate sits on: a service line (ASN-…) or a client's billing
   *  line (FBL-…, item 867) — the one door tells them apart by the id.
   *  ⛔ Never the building: the facility rate is the sum of its lines and
   *  nobody types it (TCSD-0124). */
  assignment_id: string;
  /** sub_rate is a billing line's sub rate; sub_rate_monthly a service line's. */
  field_name: 'sub_rate_monthly' | 'billing_rate' | 'sub_rate' | 'sub_hourly_rate';
  new_value: number;
  reason: string;
  /** ISO date. Omitted or blank means today. Past and future both valid. */
  effective_date?: string | null;
}

export interface RateChangeResult {
  ok: boolean;
  error: string | null;
  change_id?: string;
  status?: 'Applied' | 'Scheduled';
  effective_date?: string;
}

export function useRateChange() {
  // The signed-in employee is ALREADY in auth context. Do not re-derive an
  // identity the app can establish for itself — TCSD-0118. (The function
  // establishes it again server-side and ignores anything a caller supplies;
  // this is only so the screen can say why a control is unavailable.)
  const { employee, profile } = useAuth();
  const employeeId = employee?.employee_id ?? profile?.employee_id ?? null;
  const employeeError = employeeId
    ? null
    : 'No employee record is linked to your sign-in, so a rate change cannot be recorded against you.';

  const record = useCallback(
    async (input: RateChangeInput): Promise<RateChangeResult> => {
      if (!input.reason || input.reason.trim().length === 0) {
        return { ok: false, error: 'Say why the rate is changing.' };
      }
      if (input.new_value == null || Number.isNaN(input.new_value)) {
        return { ok: false, error: 'Enter the new figure.' };
      }
      const { data, error } = await supabase.rpc('record_rate_change', {
        p_assignment_id: input.assignment_id,
        p_field_name: input.field_name,
        p_new_value: input.new_value,
        p_reason: input.reason.trim(),
        p_effective_date: input.effective_date || null,
      });
      if (error) return { ok: false, error: error.message };
      const r = (data ?? {}) as {
        change_id?: string;
        status?: 'Applied' | 'Scheduled';
        effective_date?: string;
      };
      return {
        ok: true,
        error: null,
        change_id: r.change_id,
        status: r.status,
        effective_date: r.effective_date,
      };
    },
    [],
  );

  const cancel = useCallback(async (changeId: string, reason: string) => {
    const { error } = await supabase.rpc('cancel_rate_change', {
      p_change_id: changeId,
      p_reason: reason,
    });
    return { ok: !error, error: error?.message ?? null };
  }, []);

  return { record, cancel, employeeId, employeeError };
}
